import { Track } from "@/src/lib/tracking";
import { useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { deviceDetector } from "@ospweb/sdk-ui";
import { ListPaymentPlanRequest, PaymentPlan } from "../features/api/memberApi";
import { QUERY_KEYS } from "@config/constant";
import { useAppPayment, useBridge } from "../lib/bridge";
import { MessageType } from "@/src/lib/bridge/types";
import { OspApiClientContext } from "@features/api/client/OspApiClientProvider";

type SyncResult = {
  success: boolean;
  data?: PaymentPlan;
  error?: string;
};

// 生成缓存键的工具函数
const generateCacheKey = (params: ListPaymentPlanRequest) => {
  return [
    QUERY_KEYS.PAYMENT_LIST_PAYMENT,
    params.pay_type || "all",
    JSON.stringify(params.status || []),
    params.id || "no_id",
  ];
};

export const useGetListPayment = () => {
  const { memberApi } = useContext(OspApiClientContext);
  const queryClient = useQueryClient();
  const { send } = useBridge();
  const { openSubscriptionManagement } = useAppPayment();

  // 直接的API调用函数，不使用缓存
  const getListPaymentDirect = async (params: ListPaymentPlanRequest) => {
    try {
      // @ts-ignore
      const res = await memberApi?.payment.listPayment(params);
      return res?.data?.data?.rows || [];
    } catch (error) {
      Track.error(error, { tag: "getListPayment", msg: "error:" });
      throw error;
    }
  };

  // 带缓存的API调用函数
  const getListPaymentCached = (params: ListPaymentPlanRequest) => {
    const cacheKey = generateCacheKey(params);

    return queryClient.fetchQuery({
      queryKey: cacheKey,
      queryFn: () => getListPaymentDirect(params),
      // staleTime: 30000, // 30秒内认为数据是新鲜的
      // gcTime: 5 * 60 * 1000, // 5分钟后清理缓存
    });
  };

  // 主要的getListPayment函数，使用缓存机制
  const getListPayment = async (params: ListPaymentPlanRequest) => {
    try {
      return await getListPaymentCached(params);
    } catch (error) {
      Track.log(
        "info",
        "getListPayment",
        "cached request failed, fallback to direct:",
        { cause: String(error) },
      );
      // 如果缓存请求失败，回退到直接请求
      return await getListPaymentDirect(params);
    }
  };

  const fetchSync = (props: ListPaymentPlanRequest) => {
    let num = 5;

    return new Promise<SyncResult>(async (resolve) => {
      const attempt = async () => {
        // fetchSync仍然使用直接请求，因为它需要实时的状态检查
        const res = await getListPaymentDirect(props);
        if (res?.[0]?.status === "ACTIVE") {
          resolve({ success: true, data: res?.[0] });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.MEMBER_USE_PLAN],
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.MEMBER_FEATURE_EQUITYLIST],
          });
          // 同时更新支付列表的缓存
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.PAYMENT_LIST_PAYMENT],
          });
          // 更新memberUserDat, 是否可以试用， 取决于接口数据
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.MEMBER_FEATURE],
          });

          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.MEMBER_EQUITY_LIST],
          });
        } else {
          num--;
          if (num > 0) {
            setTimeout(async () => {
              await attempt();
            }, 3000);
          } else {
            resolve({
              success: false,
              error: "Payment not active after maximum retries",
            });
          }
        }
      };
      await attempt();
    });
  };

  // 清除特定参数的缓存
  const clearPaymentCache = (params?: ListPaymentPlanRequest) => {
    if (params) {
      const cacheKey = generateCacheKey(params);
      queryClient.removeQueries({ queryKey: cacheKey });
    } else {
      // 清除所有支付相关缓存
      queryClient.removeQueries({
        queryKey: [QUERY_KEYS.PAYMENT_LIST_PAYMENT],
      });
    }
  };

  // 取消订阅
  const cancelSubscription = async (params: {
    pay_channel: string;
    links?: string;
  }) => {
    Track.log("info", "member", "-[cancelSubscription]", {
      cause: String(params),
    });
    const isInApp = deviceDetector.isApp();
    if (params.pay_channel === "STRIPE" && params?.links !== "") {
      if (isInApp) {
        send(MessageType.OPEN_URL_EXTERNAL, {
          url: params?.links ?? "",
        });
        clearPaymentCache();
      } else {
        window.open(params?.links ?? "", "_blank");
      }
    } else {
      openSubscriptionManagement();
      clearPaymentCache();
    }
  };
  return {
    getListPayment,
    fetchSync,
    clearPaymentCache,
    getListPaymentDirect, // 暴露直接请求方法，供特殊场景使用
    cancelSubscription,
  };
};
